import {
  DownOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MobileOutlined,
  MutedOutlined,
  SearchOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Avatar, Button, Dropdown, Tooltip } from 'antd'

import type { UserInfo } from '../types'
import { openCommandPalette, paletteShortcut } from '../utils/commandPalette'

import { NotificationCenter } from './notifications/NotificationCenter'
import AppearanceMenu, { ModePill } from './ui/AppearanceMenu'

interface Props {
  user: UserInfo | null
  language: 'ar' | 'en'
  isPhone: boolean
  soundReady: boolean
  onMenu: () => void
  onLang: (lng: 'ar' | 'en') => void
  onLogout: () => void
  onProfile?: () => void
  onChangePassword?: () => void
  onUnlockSound: () => void
}

/** علم العراق مصغّراً ودائرياً — بدل الإيموجي الذي لا يُرسم على ويندوز */
function IraqFlag() {
  return (
    <svg className="sth-flag" viewBox="0 0 20 20" aria-hidden>
      <defs>
        <clipPath id="sthFlagClip">
          <circle cx="10" cy="10" r="10" />
        </clipPath>
      </defs>
      <g clipPath="url(#sthFlagClip)">
        <rect width="20" height="6.7" fill="#CE1126" />
        <rect y="6.7" width="20" height="6.6" fill="#FFFFFF" />
        <rect y="13.3" width="20" height="6.7" fill="#111111" />
        <path
          d="M6 11.2h1.2v-1.6M8.2 11.2v-1.8M9.6 11.2h1.4l.4-1.4M12.4 11.2h1.6v-1.6"
          stroke="#007A3D"
          strokeWidth="0.9"
          fill="none"
          strokeLinecap="round"
        />
      </g>
      <circle cx="10" cy="10" r="9.5" fill="none" stroke="rgba(0,0,0,0.08)" />
    </svg>
  )
}

/**
 * ترويسة ثيم «ستوديو» كالمرجع: بجهة البداية اختصارا «الإشعارات» (بعدّاد) و«المظهر»،
 * وبجهة النهاية البحث (⌘K) ثم اللغة بعلمها ومفتاح الوضع الكبسولي، ثم الحساب وزرّ الخروج الأحمر الناعم.
 */
export default function StudioHeader({
  user,
  language,
  isPhone,
  soundReady,
  onMenu,
  onLang,
  onLogout,
  onProfile,
  onChangePassword,
  onUnlockSound,
}: Props) {
  const name = language === 'ar' ? user?.fullNameAr : user?.fullNameEn

  return (
    <>
      <div className="sth-group">
        {isPhone && (
          <Button
            type="text"
            className="st-icon-btn"
            aria-label="القائمة"
            icon={<MenuUnfoldOutlined />}
            onClick={onMenu}
          />
        )}
        <NotificationCenter variant="pill" />
        <AppearanceMenu />
      </div>

      <div className="sth-group">
        <button type="button" className="st-search" onClick={openCommandPalette}>
          <SearchOutlined />
          <span className="st-search-text">
            {language === 'ar' ? 'ابحث عن أي شيء…' : 'Search anything…'}
          </span>
          <kbd>{paletteShortcut()}</kbd>
        </button>

        <span className="sth-sep" aria-hidden />

        <Dropdown
          trigger={['click']}
          menu={{
            selectedKeys: [language],
            items: [
              { key: 'ar', label: 'العربية', onClick: () => onLang('ar') },
              { key: 'en', label: 'English', onClick: () => onLang('en') },
            ],
          }}
        >
          <button type="button" className="sth-lang" aria-label="اللغة">
            {language === 'ar' ? <IraqFlag /> : <span className="sth-en">EN</span>}
            <span>{language === 'ar' ? 'عربي' : 'English'}</span>
            <DownOutlined className="sth-caret" />
          </button>
        </Dropdown>
        <ModePill />
        <Tooltip title="تطبيق الموظفين — الحضور والطلبات والإشعارات على هاتفك">
          <button
            type="button"
            className="sth-app"
            aria-label="تطبيق الموظفين"
            onClick={() => window.open('/m/', '_blank', 'noopener')}
          >
            <MobileOutlined />
            <span className="sth-app-text">{language === 'ar' ? 'التطبيق' : 'App'}</span>
          </button>
        </Tooltip>

        <span className="sth-sep" aria-hidden />

        {user && !soundReady && (
          <Tooltip title="صوت التنبيهات موقوف حتى تتفاعل مع الصفحة — انقر لتفعيله">
            <button
              type="button"
              className="sth-round warn"
              aria-label="تفعيل صوت التنبيهات"
              onClick={onUnlockSound}
            >
              <MutedOutlined />
            </button>
          </Tooltip>
        )}

        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'user',
                disabled: true,
                label: (
                  <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: 700 }}>{name}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{user?.email}</div>
                  </div>
                ),
              },
              { type: 'divider' },
              { key: 'profile', icon: <UserOutlined />, label: 'الملف الشخصي', onClick: onProfile },
              {
                key: 'password',
                icon: <LockOutlined />,
                label: 'تغيير كلمة المرور',
                onClick: onChangePassword,
              },
            ],
          }}
        >
          <button type="button" className="sth-avatar" aria-label={name ?? 'الحساب'}>
            <Avatar size={34}>{name?.charAt(0) || <UserOutlined />}</Avatar>
          </button>
        </Dropdown>

        <Tooltip title="تسجيل الخروج">
          <button
            type="button"
            className="sth-round danger"
            aria-label="تسجيل الخروج"
            onClick={onLogout}
          >
            <LogoutOutlined />
          </button>
        </Tooltip>
      </div>
    </>
  )
}
